import { useEffect, useState } from 'react';
import {
  Button, Callout, DateField, EmptyState, Heading, IconButton, Paginator, SelectField, Spinner, Table, Tag, TextField, type TableColumn,
} from '@humanforce/mango-ui-react';
import type { RequestError } from '@pay-roadrunner/api-client-core';
import type { AuditLogClient } from './auditLogHttpClient';
import type { AuditEventDto, AuditEventListPage, AuditLogSortField } from './auditLogApiTypes';

const PAGE_SIZE = 50;

const isRequestError = (error: unknown): error is RequestError =>
  typeof error === 'object' && error !== null && 'status' in error;

const errorMessage = (error: unknown) => (isRequestError(error) ? error.message : 'The audit log could not be loaded.');

const formatOccurredAt = (value: string): string =>
  new Intl.DateTimeFormat('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).format(new Date(value));

// DateField's default 'dmy' format. The range is applied in the viewer's local time, matching the Timestamp column.
const parseDate = (value: string): [number, number, number] | null => {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
  return match ? [Number(match[3]), Number(match[2]) - 1, Number(match[1])] : null;
};

const startOfLocalDay = (value: string): string | undefined => {
  const parts = parseDate(value);
  return parts ? new Date(parts[0], parts[1], parts[2], 0, 0, 0, 0).toISOString() : undefined;
};

const endOfLocalDay = (value: string): string | undefined => {
  const parts = parseDate(value);
  return parts ? new Date(parts[0], parts[1], parts[2], 23, 59, 59, 999).toISOString() : undefined;
};

const orDash = (value: string | number | null) => value ?? '—';

const AuditEventDetails = ({ row }: { row: AuditEventDto }) => (
  <dl className="mgo-stack mgo-gap-xs" style={{ margin: 0 }} data-testid={`audit-log-row-details-${row.id}`}>
    {([
      ['Description', orDash(row.description)],
      ['ID', row.id],
      ['User ID', orDash(row.userId)],
      ['Person ID', orDash(row.personId)],
      ['Roles', orDash(row.roles)],
      ['Impersonating', row.isImpersonating ? 'Yes' : 'No'],
      ['HTTP status', orDash(row.httpStatusCode)],
      ['Status description', orDash(row.statusDescription)],
    ] as [string, string | number][]).map(([label, value]) => (
      <div key={label} className="mgo-cluster mgo-gap-s">
        <dt style={{ fontWeight: 600, minWidth: '10rem' }}>{label}</dt>
        <dd style={{ margin: 0 }}>{value}</dd>
      </div>
    ))}
  </dl>
);

export interface AuditLogScreenProps {
  client: AuditLogClient;
}

/**
 * PRR-430 Administration -> Audit Log.
 *
 * Server-paged/sorted/searched grid over the PRR-421 append-only audit event log. Default view
 * is the top 50 rows ordered by date descending, per the Jira Description. UserId, PersonId,
 * PersonId and TenantSlug are free-text filters rather than dropdowns: they are effectively
 * unbounded identifier values, so a pre-populated option list would need its own "distinct values"
 * endpoint. EventName is the exception: it is a closed catalogue served by the event-names endpoint (PRR-436).
 * PRR-436 adds the date range (first filter), four filters per row and expandable rows that
 * show Description and the fields the grid omits.
 */
export const AuditLogScreen = ({ client }: AuditLogScreenProps) => {
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [userId, setUserId] = useState('');
  const [personId, setPersonId] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [tenantSlug, setTenantSlug] = useState('');
  const [eventName, setEventName] = useState('');
  const [eventNames, setEventNames] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<AuditLogSortField>('occurredAtUtc');
  const [sortDescending, setSortDescending] = useState(true);

  const [result, setResult] = useState<AuditEventListPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  // The Event dropdown is a convenience: if the catalogue cannot be loaded the grid still works, with no options to pick.
  useEffect(() => {
    const controller = new AbortController();
    client.listEventNames(controller.signal).then(setEventNames, () => { if (!controller.signal.aborted) setEventNames([]); });
    return () => controller.abort();
  }, [client]);

  // Debounced so typing does not issue a request per keystroke. Filter and sort
  // changes ride the same effect; the 250ms wait is imperceptible for them.
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLoading(true);
      void (async () => {
        try {
          const response = await client.listAuditEvents({
            search: search.trim() || undefined,
            userId: userId.trim() || undefined,
            personId: personId.trim() || undefined,
            userEmail: userEmail.trim() || undefined,
            tenantSlug: tenantSlug.trim() || undefined,
            eventName: eventName.trim() || undefined,
            startDate: startOfLocalDay(startDate),
            endDate: endOfLocalDay(endDate),
            page,
            pageSize: PAGE_SIZE,
            sortBy,
            sortDescending,
          }, controller.signal);
          setResult(response);
          setError('');
        } catch (caught) {
          if (controller.signal.aborted) return;
          setResult(null);
          setError(errorMessage(caught));
        } finally {
          if (!controller.signal.aborted) setLoading(false);
        }
      })();
    }, 250);
    return () => { controller.abort(); clearTimeout(timer); };
  }, [client, endDate, eventName, page, personId, reloadToken, search, sortBy, sortDescending, startDate, tenantSlug, userEmail, userId]);

  // Reset to the first page whenever the result set shape could change.
  useEffect(() => { setPage(1); }, [endDate, eventName, personId, search, sortBy, sortDescending, startDate, tenantSlug, userEmail, userId]);

  // DataTable treats any key present in `expandedRows` as expanded, so collapsing must remove the key rather than set it to false.
  const toggleRow = (id: string) => setExpanded(({ [id]: wasExpanded, ...rest }) => (wasExpanded ? rest : { ...rest, [id]: true }));

  const columns: TableColumn[] = [
    {
      field: 'expander', header: '',
      body: (row: AuditEventDto) => (
        <IconButton
          icon={expanded[row.id] ? 'indicator-up' : 'indicator-down'}
          label={expanded[row.id] ? 'Collapse row' : 'Expand row'}
          variant="tertiary"
          size="small"
          onClick={() => toggleRow(row.id)}
        />
      ),
    },
    { field: 'occurredAtUtc', header: 'Timestamp (local time)', sortable: true, body: (row: AuditEventDto) => formatOccurredAt(row.occurredAtUtc) },
    { field: 'eventName', header: 'Event', sortable: true },
    { field: 'userEmail', header: 'User email', sortable: true, body: (row: AuditEventDto) => row.userEmail ?? '—' },
    { field: 'tenantSlug', header: 'Tenant', sortable: true, body: (row: AuditEventDto) => row.tenantSlug ?? '—' },
    {
      field: 'succeeded', header: 'Result', sortable: true,
      body: (row: AuditEventDto) => <Tag label={row.succeeded ? 'Succeeded' : 'Failed'} tone={row.succeeded ? 'green' : 'red'} />,
    },
    { field: 'correlationId', header: 'Correlation ID', body: (row: AuditEventDto) => row.correlationId ?? '—' },
  ];

  return (
    <div className="mgo-stack mgo-gap-l" data-testid="audit-log-screen">
      <div className="mgo-stack mgo-gap-xs">
        <Heading level={2}>Audit Log</Heading>
      </div>

      <TextField
        label="Search"
        hideLabel
        placeholder="Search by correlation ID, user ID, person ID, user email, event or description"
        leadingIcon="search"
        clearable
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        onClear={() => setSearch('')}
        testId="audit-log-search"
      />

      <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-4 mgo-gap-s" data-testid="audit-log-filters">
        <DateField label="Start date" value={startDate} max={endDate || undefined} onChange={setStartDate} onClear={() => setStartDate('')} testId="audit-log-filter-startDate" />
        <DateField label="End date" value={endDate} min={startDate || undefined} onChange={setEndDate} onClear={() => setEndDate('')} testId="audit-log-filter-endDate" />
        <TextField label="User ID" value={userId} onChange={(event) => setUserId(event.target.value)} clearable onClear={() => setUserId('')} testId="audit-log-filter-userId" />
        <TextField label="Person ID" value={personId} onChange={(event) => setPersonId(event.target.value)} clearable onClear={() => setPersonId('')} testId="audit-log-filter-personId" />
        <TextField label="User email" value={userEmail} onChange={(event) => setUserEmail(event.target.value)} clearable onClear={() => setUserEmail('')} testId="audit-log-filter-userEmail" />
        <TextField label="Tenant" value={tenantSlug} onChange={(event) => setTenantSlug(event.target.value)} clearable onClear={() => setTenantSlug('')} testId="audit-log-filter-tenantSlug" />
        <SelectField
          label="Event"
          placeholder="All events"
          value={eventName || null}
          options={eventNames.map((name) => ({ label: name, value: name }))}
          onChange={(next) => setEventName(typeof next === 'string' ? next : '')}
          clearable
          onClear={() => setEventName('')}
          testId="audit-log-filter-eventName"
        />
      </div>

      {error ? (
        <Callout
          tone="critical"
          title="The audit log could not be loaded"
          action={<Button label="Retry" variant="secondary" onClick={() => setReloadToken((token) => token + 1)} testId="audit-log-retry" />}
        >
          {error}
        </Callout>
      ) : null}

      {loading && !result ? (
        <div className="mgo-cluster mgo-gap-s" aria-live="polite" aria-busy="true">
          <Spinner /><span>Loading the audit log…</span>
        </div>
      ) : null}

      {!error && result && result.totalCount === 0 && !loading ? (
        <EmptyState variant="no-data" title="No audit events match your search" description="Try a different search term or clear the filters." />
      ) : null}

      {!error && result && result.totalCount > 0 ? (
        <>
          <Table
            value={result.items}
            columns={columns}
            ptProps={{ dataKey: 'id', expandedRows: expanded, rowExpansionTemplate: (row: AuditEventDto) => <AuditEventDetails row={row} /> }}
            loading={loading}
            sortField={sortBy}
            sortOrder={sortDescending ? -1 : 1}
            lazy
            onLazyLoad={(event) => {
              if (!event.sortField || !event.sortOrder) {
                setSortBy('occurredAtUtc');
                setSortDescending(true);
                return;
              }
              setSortBy(event.sortField as AuditLogSortField);
              setSortDescending(event.sortOrder === -1);
            }}
            emptyMessage="No audit events match your search."
            testId="audit-log-table"
          />
          {result.totalPages > 1 ? (
            <Paginator
              first={(result.page - 1) * result.pageSize}
              itemsPerPage={result.pageSize}
              totalRecords={result.totalCount}
              onPageChange={(event) => setPage(event.page + 1)}
              testId="audit-log-paginator"
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
};
